import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Storage key for scroll positions
const SCROLL_STORAGE_KEY = 'spirit-of-shabbat-scroll-positions';

/**
 * Hook to save and restore scroll positions when navigating between routes.
 * Uses sessionStorage to persist positions across page refreshes.
 *
 * @example
 * // In a layout component:
 * useScrollRestoration();
 */
export function useScrollRestoration() {
  const location = useLocation();
  const previousPath = useRef(location.pathname);

  // Save scroll position before navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveScrollPosition(location.pathname);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [location.pathname]);

  // Save/restore scroll on route change
  useEffect(() => {
    // Save scroll position of the previous page
    if (previousPath.current !== location.pathname) {
      saveScrollPosition(previousPath.current);
    }

    // Check if this is a back/forward navigation
    const isPopState = window.history.state?.usr?.scrollY !== undefined;

    // Restore scroll position for the current page
    const savedPosition = getScrollPosition(location.pathname);

    // Use a small delay to ensure the new content has rendered
    const timer = setTimeout(() => {
      if (isPopState && window.history.state?.usr?.scrollY !== undefined) {
        // Use the browser's saved position for back/forward
        window.scrollTo(0, window.history.state.usr.scrollY);
      } else if (savedPosition !== null) {
        // Use our saved position
        window.scrollTo(0, savedPosition);
      } else {
        // New page, scroll to top
        window.scrollTo(0, 0);
      }
    }, 50);

    previousPath.current = location.pathname;

    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Also save on popstate (back/forward navigation)
  useEffect(() => {
    const handlePopState = () => {
      // Save current scroll position with the history state
      const currentState = window.history.state || {};
      window.history.replaceState(
        { ...currentState, usr: { ...currentState.usr, scrollY: window.scrollY } },
        ''
      );
    };

    // Save scroll position periodically (handles in-page scrolling)
    const saveInterval = setInterval(() => {
      saveScrollPosition(location.pathname);
    }, 1000);

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      clearInterval(saveInterval);
    };
  }, [location.pathname]);
}

/**
 * Get stored scroll positions from sessionStorage
 */
function getScrollPositions(): Record<string, number> {
  try {
    const stored = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

/**
 * Save scroll position for a path
 */
function saveScrollPosition(path: string) {
  try {
    const positions = getScrollPositions();
    positions[path] = window.scrollY;
    sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(positions));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Get scroll position for a path
 */
function getScrollPosition(path: string): number | null {
  const positions = getScrollPositions();
  return positions[path] ?? null;
}

/**
 * Clear scroll positions (e.g., on logout)
 */
export function clearScrollPositions() {
  try {
    sessionStorage.removeItem(SCROLL_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}
